/**
 * Time tracking (T-77): the minutes people spend active in jflo, one row per person per minute
 * (`time_activity`). The web app and the Chrome extension decide which minutes count
 * (shared/activity-tracker.ts, extension/lib/background-activity.ts) and send them to POST /api/activity and POST /api/v1/activity; the Worker
 * checks them (worker/src/services/time-tracking/). Shared here: the limits both sides keep, the areas,
 * the records a minute can be about, and how a page's address names them.
 */

/** A tab stops counting after this long with no typing, clicking, scrolling or touching. */
export const ACTIVITY_IDLE_MS = 2 * 60_000;
/** Receiving has gaps between scans (unpacking a box), so its pages keep counting for longer. */
export const RECEIVING_IDLE_MS = 5 * 60_000;
/** The most minutes one POST takes. */
export const MAX_ACTIVITY_MINUTES = 120;
/** Minutes older than this are refused (and the browser stops holding them). */
export const MAX_ACTIVITY_AGE_MS = 24 * 60 * 60_000;
/** The longest `detail` kept (the extension's site, such as admin.shopify.com). */
export const MAX_ACTIVITY_DETAIL = 100;

export const MINUTE_MS = 60_000;

/** Where in jflo a minute was spent, in the order the report lists them when tied. */
export const TIME_AREAS = [
  'receiving',
  'planning',
  'purchase-orders',
  'imports',
  'products',
  'new-products',
  'inbox',
  'tasks',
  'suppliers',
  'trade',
  'marketing',
  'labels',
  'policies',
  'reports',
  'settings',
  'extension',
  'other',
] as const;
export type TimeArea = (typeof TIME_AREAS)[number];

export const TIME_AREA_LABELS: Record<TimeArea, string> = {
  receiving: 'Receiving',
  planning: 'Planning',
  'purchase-orders': 'Purchase orders',
  imports: 'Imports',
  products: 'Products',
  'new-products': 'New products',
  inbox: 'Inbox',
  tasks: 'Tasks',
  suppliers: 'Suppliers',
  trade: 'Trade',
  marketing: 'Marketing',
  labels: 'Labels',
  policies: 'Policies',
  reports: 'Reports and home',
  settings: 'Settings',
  extension: 'Chrome extension',
  other: 'Other',
};

export function isTimeArea(value: unknown): value is TimeArea {
  return typeof value === 'string' && (TIME_AREAS as readonly string[]).includes(value);
}

/** The records a minute can be about. */
export const TIME_RECORD_KINDS = [
  'receiving_session',
  'purchase_order',
  'planning_order',
  'shipment',
  'conversation',
  'product',
  'product_batch',
  'supplier',
  'task',
] as const;
export type TimeRecordKind = (typeof TIME_RECORD_KINDS)[number];

export const TIME_RECORD_KIND_LABELS: Record<TimeRecordKind, string> = {
  receiving_session: 'Receiving sessions',
  purchase_order: 'Purchase orders',
  planning_order: 'Planning orders',
  shipment: 'Shipments',
  conversation: 'Inbox conversations',
  product: 'Products',
  product_batch: 'New product batches',
  supplier: 'Suppliers',
  task: 'Tasks',
};

export function isTimeRecordKind(value: unknown): value is TimeRecordKind {
  return typeof value === 'string' && (TIME_RECORD_KINDS as readonly string[]).includes(value);
}

/** Where a minute is sent from. */
export type TimeSource = 'app' | 'extension';

export interface TimeRecordRef {
  kind: TimeRecordKind;
  id: string;
}

/** Where a tab is: its area, and the record open in it when there is one. */
export interface ActivityContext {
  area: TimeArea;
  record_kind: TimeRecordKind | null;
  record_id: string | null;
  /** The extension's site (a hostname); null in the web app. */
  detail: string | null;
}

/** One counted minute as a tab sends it. */
export interface ActivityMinute extends ActivityContext {
  /** The minute's start, ISO (seconds zero). */
  minute: string;
}

/** How long a tab in this area keeps counting after the last input. */
export function idleMsFor(area: TimeArea): number {
  return area === 'receiving' ? RECEIVING_IDLE_MS : ACTIVITY_IDLE_MS;
}

/** The start of the minute `ms` falls in. */
export function minuteStart(ms: number): number {
  return Math.floor(ms / MINUTE_MS) * MINUTE_MS;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isActivityUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value);
}

const record = (kind: TimeRecordKind, id: string | undefined | null): TimeRecordRef | null =>
  isActivityUuid(id) ? { kind, id: id.toLowerCase() } : null;

/** The first path segment's area, for pages with no more specific rule below. */
const TOP_AREAS: Record<string, TimeArea> = {
  '': 'reports',
  receiving: 'receiving',
  planning: 'planning',
  imports: 'imports',
  products: 'products',
  'products-bulk-edit': 'products',
  'product-details': 'products',
  'variant-details': 'products',
  'product-types': 'products',
  vendors: 'products',
  collections: 'products',
  'collections-bulk-edit': 'products',
  'collections-gaps': 'products',
  swatches: 'products',
  media: 'products',
  'product-batches': 'new-products',
  'product-content-queue': 'new-products',
  inbox: 'inbox',
  tasks: 'tasks',
  suppliers: 'suppliers',
  trade: 'trade',
  wholesale: 'trade',
  marketing: 'marketing',
  'landing-pages': 'marketing',
  labels: 'labels',
  policies: 'policies',
  reports: 'reports',
  'ai-usage': 'reports',
  'team-settings': 'settings',
  team: 'settings',
  integrations: 'settings',
  'inbox-settings': 'settings',
  'ai-prompts': 'settings',
  sync: 'settings',
  'xero-posting': 'settings',
};

/**
 * Where a page's address puts a minute: its area, and the record it shows when the address names one
 * (`/receiving/<id>`, `/inbox/<id>`, `/planning?order=<id>`, `/planning/purchase-orders?po=<id>`,
 * `/product-details/<id>`…).
 */
export function activityFromPath(pathname: string, search = ''): { area: TimeArea; record: TimeRecordRef | null } {
  const parts = pathname.split('/').filter(Boolean);
  const [top = '', second, third] = parts;
  const params = new URLSearchParams(search);
  if (top === 'planning') {
    // The PO list's open order is `?po=<id>` (its edit dialog).
    if (second === 'purchase-orders') return { area: 'purchase-orders', record: record('purchase_order', params.get('po')) };
    return { area: 'planning', record: record('planning_order', params.get('order')) };
  }
  if (top === 'receiving') return { area: 'receiving', record: record('receiving_session', second) };
  if (top === 'inbox') return { area: 'inbox', record: record('conversation', second) };
  if (top === 'imports') {
    if (second === 'shipments') return { area: 'imports', record: record('shipment', third) };
    if (second === 'suppliers') return { area: 'imports', record: record('supplier', third) };
    return { area: 'imports', record: null };
  }
  if (top === 'product-details') return { area: 'products', record: record('product', second) };
  if (top === 'product-batches') return { area: 'new-products', record: record('product_batch', second) };
  if (top === 'suppliers' && second !== 'catalogues') return { area: 'suppliers', record: record('supplier', second) };
  if (top === 'tasks') return { area: 'tasks', record: record('task', second) };
  return { area: TOP_AREAS[top] ?? 'other', record: null };
}

// ---- The extension's tracked sites (team settings `timeTrackingSites`) ----

/**
 * The sites the Chrome extension counts time beside, until a team edits its list. Anywhere else the
 * panel counts nothing, so the pages people visit aren't kept.
 */
export const DEFAULT_TRACKED_SITES: readonly string[] = ['admin.shopify.com'];
/** The most sites a team's list holds. */
export const MAX_TRACKED_SITES = 50;

const HOST_RE = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/;
const PATH_RE = /^[a-z0-9._~%!$&'()*+,;=:@/-]*$/;

/**
 * One entry of the list as it's kept: a site, and optionally the start of its paths, lower case, with
 * no scheme, `www.`, query or trailing slash ("https://www.Supplier.com/shop/" → "supplier.com/shop").
 * Null when it isn't a site.
 */
export function cleanTrackedSite(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  let text = value.trim().toLowerCase();
  if (!text) return null;
  text = text.replace(/^[a-z][a-z0-9+.-]*:\/\//, '').replace(/[?#].*$/, '');
  const slash = text.indexOf('/');
  const host = (slash < 0 ? text : text.slice(0, slash)).replace(/:\d+$/, '').replace(/^www\./, '');
  const path = slash < 0 ? '' : text.slice(slash).replace(/\/+$/, '');
  if (!HOST_RE.test(host) || !PATH_RE.test(path)) return null;
  const site = host + path;
  return site.length > MAX_ACTIVITY_DETAIL ? null : site;
}

/**
 * The team's list from its settings: the default until it's been saved, then exactly what was saved
 * (an empty list means the extension counts no time).
 */
export function trackedSitesFrom(settings: unknown): string[] {
  const raw =
    settings && typeof settings === 'object' && !Array.isArray(settings)
      ? (settings as Record<string, unknown>).timeTrackingSites
      : undefined;
  if (!Array.isArray(raw)) return [...DEFAULT_TRACKED_SITES];
  const sites: string[] = [];
  for (const entry of raw) {
    const site = cleanTrackedSite(entry);
    if (site && !sites.includes(site) && sites.length < MAX_TRACKED_SITES) sites.push(site);
  }
  return sites;
}

/** The list as typed, one site a line (or comma-separated): the clean sites, once each, and the first line that isn't one. */
export function parseTrackedSitesText(text: string): { sites: string[]; bad: string | null } {
  const sites: string[] = [];
  for (const line of text.split(/[\n,]+/)) {
    if (!line.trim()) continue;
    const site = cleanTrackedSite(line);
    if (!site) return { sites, bad: line.trim() };
    if (!sites.includes(site)) sites.push(site);
  }
  return { sites, bad: null };
}

/**
 * The entry of `sites` an address is on (the most specific when several are), or null. A site covers
 * its subdomains ("bravehardy.com" covers www. and shop.), and a path covers the pages below it.
 */
export function trackedSiteFor(url: string | null | undefined, sites: readonly string[]): string | null {
  if (!url) return null;
  let host: string;
  let path: string;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
    host = parsed.hostname.toLowerCase().replace(/^www\./, '');
    path = parsed.pathname.toLowerCase().replace(/\/+$/, '');
  } catch {
    return null;
  }
  let best: string | null = null;
  for (const site of sites) {
    const slash = site.indexOf('/');
    const siteHost = slash < 0 ? site : site.slice(0, slash);
    const sitePath = slash < 0 ? '' : site.slice(slash);
    if (host !== siteHost && !host.endsWith(`.${siteHost}`)) continue;
    if (sitePath && path !== sitePath && !path.startsWith(`${sitePath}/`)) continue;
    if (!best || site.length > best.length) best = site;
  }
  return best;
}

// ---- The report (GET /api/reports/time-spent) ----

export interface TimePerson {
  id: string;
  name: string;
}

export interface TimeAreaTotal {
  area: TimeArea;
  minutes: number;
  people: number;
  /** Days with any time in the area (in the team's timezone). */
  days: number;
}

export interface TimePersonTotal {
  person: TimePerson;
  minutes: number;
  days: number;
  /** The area they spent most time in. */
  top_area: TimeArea | null;
}

export interface TimeSiteTotal {
  /** The tracked site (an entry of the team's list) the extension counted the minute on; null when it didn't know. */
  site: string | null;
  minutes: number;
}

export interface TimeRecordRow {
  kind: TimeRecordKind;
  id: string;
  /** The record's name (PO number, subject, title…); null when it has gone or is hidden. */
  name: string | null;
  /** The name is hidden from the caller (a conversation's subject without inbox access). */
  name_hidden: boolean;
  minutes: number;
  people: number;
  first_at: string;
  last_at: string;
  /** Receiving sessions: their purchase orders' numbers (comma-separated); null otherwise. */
  purchase_order_number: string | null;
  /** Purchase orders: time on its planning order, its receiving sessions and its Imports shipments, beside its own. */
  planning_minutes: number | null;
  receiving_minutes: number | null;
  shipment_minutes: number | null;
  /** Conversations: replies sent in the range (by the person filtered to, when there is one). */
  replies: number | null;
}

/** One kind of record's count, and average and median minutes, over every record in range. */
export interface TimeRecordStats {
  kind: TimeRecordKind;
  count: number;
  average: number;
  median: number;
  /** Conversations: replies sent in the range across them all. */
  replies: number | null;
}

/** Email time by the inbox's Topic labels; a conversation with several counts on each. */
export interface TimeTopicTotal {
  /** The Topic label's name; null for conversations with none. */
  topic: string | null;
  conversations: number;
  minutes: number;
  replies: number;
}

/** One person's time on inbox conversations and the replies they sent from jflo. */
export interface TimeEmailPersonTotal {
  person: TimePerson;
  conversations: number;
  minutes: number;
  /** Replies they sent from jflo in the range, on conversations with time (Gmail's own replies have no person). */
  replies: number;
}

/** One person's email time and replies on one Topic label's conversations; one with several counts on each. */
export interface TimeEmailPersonTopicTotal {
  person: TimePerson;
  /** The Topic label; null for conversations with none. */
  topic: string | null;
  conversations: number;
  minutes: number;
  replies: number;
}

export interface TimeSpentReport {
  /** The range asked for, `YYYY-MM-DD`, both days included, in the team's timezone. */
  from: string;
  to: string;
  minutes: number;
  people: TimePerson[];
  by_area: TimeAreaTotal[];
  by_person: TimePersonTotal[];
  by_site: TimeSiteTotal[];
  /** Email by Topic label; empty for people without inbox access (label names are inbox data). */
  by_topic: TimeTopicTotal[];
  /** Email by person: time on conversations, replies sent and so time per reply. */
  email_by_person: TimeEmailPersonTotal[];
  /** Email by person and Topic label; empty for people without inbox access (label names are inbox data). */
  email_by_person_topic: TimeEmailPersonTopicTotal[];
  /** The busiest records of each kind (at most 5,000 a kind). */
  records: TimeRecordRow[];
  /** Each kind's figures over all its records, including any `records` leaves out. */
  record_stats: TimeRecordStats[];
  /** Whether some kind had more records than `records` holds. */
  records_truncated: boolean;
}
